/**
 * Transaction detail page (BUILD_SPEC.md §6.3, v1.1 B4).
 *
 * Renders the full action history for one transaction at
 * /transactions/:id, in contrast to the dashboard card which only
 * shows the latest 3. Reachable by all authenticated users — ADMIN,
 * EMPLOYEE, and DISPLAY (TV kiosk). Layout mirrors the dashboard card
 * for readability at TV distance: dominant owner name, status pill,
 * region + service type chips, then the action history newest-first.
 *
 * Per-action keyword highlighting reuses `KeywordHighlight` so the
 * §9.2 detector wins consistently between card and detail views. The
 * status pill reuses the v1.1 B2 color rules.
 *
 * Future-coupling notes:
 *   • The takleef-details section (assigning party / notes / external
 *     confirm link) is wired in B5 once the parser populates the new
 *     fields on Transaction.
 *   • Export-this-transaction button is admin-only and reuses the
 *     existing /api/export/transactions/:id endpoint.
 *   • The header's `state.from` deep-link behavior (v1.1 M11) means
 *     unauth users bouncing through /login can land back here after
 *     re-auth — no special handling needed.
 */

import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AlertOctagon, ArrowLeft, Download, ExternalLink } from "lucide-react";
import { getTransaction } from "@/api/transactions";
import { downloadTransactionExport } from "@/api/exports";
import { useAuth } from "@/hooks/useAuth";
import { ActionRow } from "@/components/ActionRow";

/** v1.1.1 (2026-05-14) status-pill colors. Source is
 *  `Transaction.requestStatus`, the "حالة الطلب" from the top
 *  "معلومات الطلب" block of the municipality result page -- distinct from
 *  the per-action status column. Duplicated here rather than exported from
 *  TransactionCard so the two surfaces stay decoupled. */
function pillColors(status: string): string {
  if (status === "مكتمل") return "bg-mint dark:bg-mint-dark text-fg dark:text-fg-dark";
  if (status === "مرفوض") return "bg-rose dark:bg-rose-dark text-fg dark:text-fg-dark";
  if (status === "مكلف") return "bg-peach dark:bg-peach-dark text-fg dark:text-fg-dark";
  return "bg-hairline/40 dark:bg-hairline-dark/60 text-fg dark:text-fg-dark";
}

export function TransactionDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["transaction", id],
    queryFn: () => getTransaction(id!),
    enabled: Boolean(id),
  });

  const exportMutation = useMutation({
    mutationFn: () => downloadTransactionExport(id!),
  });

  if (query.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <p className="text-sm text-muted dark:text-muted-dark">{t("detail.loading")}</p>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-rejection-bg dark:bg-rejection-dark-bg/40">
            <AlertOctagon className="h-8 w-8 text-rejection dark:text-rejection-dark" aria-hidden />
          </div>
          <h2 className="text-xl font-bold text-fg dark:text-fg-dark mb-2">
            {t("detail.notFoundTitle")}
          </h2>
          <p className="text-sm text-muted dark:text-muted-dark mb-4">{t("detail.notFoundHint")}</p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-4 py-2 text-sm font-semibold"
          >
            <ArrowLeft className="h-4 w-4 rtl:hidden" />
            <ArrowLeft className="h-4 w-4 ltr:hidden scale-x-[-1]" />
            {t("detail.back")}
          </Link>
        </div>
      </div>
    );
  }

  const { transaction, actions } = query.data;
  const requestStatus = transaction.requestStatus;
  const isAdmin = user?.role === "ADMIN";

  return (
    // v1.1 A5: AppLayout fixes the viewport at `h-screen overflow-hidden`,
    // so the detail page owns its own vertical scroller via `h-full
    // overflow-y-auto`. A 66-action history is much taller than the
    // viewport, and without this the trailing rows would be clipped.
    <div className="h-full overflow-y-auto mx-auto max-w-4xl px-4 py-4 space-y-4">
      {/* Top bar: back link + action buttons. `state.logout=false` is
          implicit — we just want a plain back nav. */}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
        >
          {/* The arrow flips with dir via the rtl:hidden/ltr:hidden
              pair so the affordance always points "leading→trailing"
              (i.e., right-to-left in Arabic). */}
          <ArrowLeft className="h-4 w-4 rtl:hidden" aria-hidden />
          <ArrowLeft className="h-4 w-4 ltr:hidden scale-x-[-1]" aria-hidden />
          {t("detail.back")}
        </button>
        {isAdmin ? (
          <button
            type="button"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline dark:border-hairline-dark px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40 disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden />
            {exportMutation.isPending ? t("export.exporting") : t("export.transaction")}
          </button>
        ) : null}
      </div>

      {/* Header card — owner, identifiers, region/service chips, and
          the v1.1 B2 status pill from the latest action. Layout mirrors
          the dashboard TransactionCard header so a user navigating
          between the two has zero relearning cost. */}
      <article className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <h2 className="rtl-isolate text-xl font-bold text-fg dark:text-fg-dark">
            {transaction.ownerName ?? transaction.requestNumber}
          </h2>
          {requestStatus ? (
            <span
              className={`rtl-isolate shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-bold ${pillColors(requestStatus)}`}
            >
              {requestStatus}
            </span>
          ) : null}
        </div>
        <div className="rtl-isolate mt-1 text-sm text-muted dark:text-muted-dark">
          {transaction.requestNumber} / {transaction.year}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="rtl-isolate inline-flex items-center rounded-full bg-hairline/40 dark:bg-hairline-dark/60 px-2 py-0.5 text-xs font-medium text-fg dark:text-fg-dark">
            {transaction.region}
          </span>
          <span className="rtl-isolate inline-flex items-center rounded-full bg-hairline/40 dark:bg-hairline-dark/60 px-2 py-0.5 text-xs font-medium text-fg dark:text-fg-dark">
            {transaction.serviceType}
          </span>
        </div>
        {/* followUpPhone is PII — admin only. The server enforces the
            same (it returns the field but the chrome shouldn't surface
            it to non-admin users). */}
        {isAdmin ? (
          <div className="rtl-isolate mt-2 text-sm text-muted dark:text-muted-dark">
            {t("tx.followUpPhone")}: {transaction.followUpPhone}
          </div>
        ) : null}
      </article>

      {/* v1.1 B5 — takleef section. Only renders when the scraper has
          persisted the fields (which only happens when actions[0]
          contains "تكليف"). The detail page shows the section inline
          rather than behind a flip because vertical space is cheap
          here. The external link is `target="_blank"` per spec; the
          dashboard never confirms on the user's behalf. */}
      {transaction.assigningParty ||
      transaction.assigningNotes ||
      transaction.assigningConfirmUrl ? (
        <section className="rounded-xl border border-peach dark:border-peach-dark bg-peach/30 dark:bg-peach-dark/30 p-4">
          <h3 className="text-base font-bold text-fg dark:text-fg-dark mb-3">
            {t("takleef.viewMore")}
          </h3>
          <dl className="space-y-3 text-sm">
            {transaction.assigningParty ? (
              <div>
                <dt className="text-xs text-muted dark:text-muted-dark">
                  {t("takleef.party")}
                </dt>
                <dd className="rtl-isolate text-fg dark:text-fg-dark">
                  {transaction.assigningParty}
                </dd>
              </div>
            ) : null}
            {transaction.assigningNotes ? (
              <div>
                <dt className="text-xs text-muted dark:text-muted-dark">
                  {t("takleef.notes")}
                </dt>
                <dd className="rtl-isolate text-fg dark:text-fg-dark">
                  {transaction.assigningNotes}
                </dd>
              </div>
            ) : null}
            {transaction.assigningConfirmUrl ? (
              <div>
                <a
                  href={transaction.assigningConfirmUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  {t("takleef.openConfirm")}
                </a>
                <p className="mt-1 text-xs text-muted dark:text-muted-dark">
                  {t("takleef.linkHint")}
                </p>
              </div>
            ) : null}
          </dl>
        </section>
      ) : null}

      {/* Action history — newest first. The API returns the rows in
          position-desc already (see GET /api/transactions/:id), so we
          can map directly. Empty state matches the card's. */}
      <section className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-4 shadow-sm">
        <header className="mb-3">
          <h3 className="text-base font-bold text-fg dark:text-fg-dark">
            {t("detail.history")}
          </h3>
          <p className="text-xs text-muted dark:text-muted-dark">
            {t("detail.historyCount", { count: actions.length })}
          </p>
        </header>
        {actions.length === 0 ? (
          <p className="text-sm italic text-muted dark:text-muted-dark">
            {t("dashboard.noActions")}
          </p>
        ) : (
          <div>
            {actions.map((a) => (
              <ActionRow key={a.id} action={a} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
